'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import Icon from '@/components/shared/Icons';

type Props = {
  videoId?: string;
  uniqueId: string | undefined;
  videoTag: string;
  playVideoText: string;
};
// extending the existing Window interface to include the new property _wq for wistia player
declare global {
  interface Window {
    _wq: unknown[];
  }
}

export default function VideoPlayer({ videoId = '', uniqueId, videoTag, playVideoText }: Props): JSX.Element {
  const [showPlayIcon, setSetShowPlayIcon] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isContainerVisible, setIsContainerVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current == null) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry.isIntersecting && !isContainerVisible) {
        setIsContainerVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(containerRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadScript = useCallback(async () => {
    if (containerRef.current == null) {
      return;
    }
    const wistiaScript = document.createElement('script');
    wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js';
    wistiaScript.async = true;
    containerRef.current.append(wistiaScript);
  }, []);

  useEffect(() => {
    if (isContainerVisible) {
      loadScript();
    }
  }, [isContainerVisible, loadScript]);

  useEffect(() => {
    window._wq = window._wq || [];
    window._wq.push({
      id: uniqueId,
      options: {
        autoPlay: false,
        controlsVisibleOnLoad: false,
        fullscreenButton: true,
        playButton: false,
        smallPlayButton: true,
        volumeControl: true,
        wmode: 'transparent',
        fitStrategy: 'cover'
      },
      onReady(video) {
        setIsVideoLoaded(true);
        if (video) {
          video.bind('play', () => {
            setSetShowPlayIcon(false);
          });
        }
      }
    });
  }, [uniqueId]);

  const removeIcon = useCallback(
    async (e: React.SyntheticEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isVideoLoaded && isContainerVisible) {
        setSetShowPlayIcon(false);
        /* @ts-expect-error Cannot find name 'Wistia' */
        const video = Wistia.api(uniqueId);
        video.play();
      }
    },
    [isContainerVisible, isVideoLoaded, uniqueId]
  );

  const handleClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    // don't want wrapper div to be accessible to screen reader
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={`wistia_responsive_padding relative ${
        isVideoLoaded ? 'aspect-video' : 'aspect-[2/1]'
      } h-full w-full flex-shrink-0 sm:min-h-[152px] sm:max-w-full`}
      key={uniqueId}
      onClick={handleClick}
    >
      {isVideoLoaded ? (
        <div className="absolute z-10 m-2 items-center rounded-xl bg-background px-2 text-b-xs text-brand-60">
          {videoTag}
        </div>
      ) : null}
      {showPlayIcon && isVideoLoaded ? (
        <button
          type="button"
          aria-label={playVideoText}
          className="flex h-full w-full cursor-pointer items-center justify-center"
          onClick={removeIcon}
        >
          <Icon id="play-video" className="z-10 h-16 w-16" />
        </button>
      ) : null}

      <div
        key={videoId}
        id={uniqueId}
        className={`wistia_embed wistia_async_${videoId} absolute left-0 top-0 flex h-full w-full justify-center rounded-lg bg-background ${
          isVideoLoaded ? 'pt-0' : 'pt-0.5'
        }`}
      />
    </div>
  );
}
